function throttle (func, wait = 100) {
  let timer = null
  return function (...args) {
    if (timer === null) {
      timer = setTimeout(() => {
        func.apply(this, args)
        timer = null
      }, wait)
    }
  }
}

function debounce (fn, wait) {
  let t
  return (...args) => {
    clearTimeout(t)
    t = setTimeout(() => fn.apply(this, args), wait)
  }
}

const serializeForm = form => {
  const obj = {}
  const formData = new FormData(form)
  for (const key of formData.keys()) {
    obj[key] = formData.get(key)
  }
  return JSON.stringify(obj)
}

function fetchConfig (type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` }
  }
}

function getFocusableElements (container) {
  return Array.from(
    container.querySelectorAll(
      'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
    )
  )
}

const trapFocusHandlers = {}

function trapFocus (container, elementToFocus = container) {
  const elements = getFocusableElements(container)
  const first = elements[0]
  const last = elements[elements.length - 1]

  removeTrapFocus()

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    ) {
      return
    }

    document.addEventListener('keydown', trapFocusHandlers.keydown)
  }

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown)
  }

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault()
      first.focus()
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault()
      last.focus()
    }
  }

  document.addEventListener('focusout', trapFocusHandlers.focusout)
  document.addEventListener('focusin', trapFocusHandlers.focusin)

  elementToFocus.focus()
}

function removeTrapFocus (elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin)
  document.removeEventListener('focusout', trapFocusHandlers.focusout)
  document.removeEventListener('keydown', trapFocusHandlers.keydown)

  if (elementToFocus) elementToFocus.focus()
}

function onKeyUpEscape (event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return

  const openDetailsElement = event.target.closest('details[open]')
  if (!openDetailsElement) return

  const summaryElement = openDetailsElement.querySelector('summary')
  openDetailsElement.removeAttribute('open')
  summaryElement.setAttribute('aria-expanded', false)
  summaryElement.focus()
}

function isJson (item) {
  let value = typeof item !== 'string' ? JSON.stringify(item) : item
  try {
    value = JSON.parse(value)
  } catch (e) {
    return false
  }

  return typeof value === 'object' && value !== null
}

/**
 * The function fixes cart errors that result from purchase limit changes while an item is in the user's cart
 * @param error - The `error` parameter is the JSON response from the server when purchase limit errors are present
 */

let fixAttempt = 0
let firstError
let allErrors = []
function fixCartWithPurchaseLimitError (productsWithErrors) {
  // Get products to fix from error message
  let products = productsWithErrors
  if (fixAttempt === 0) {
    firstError = productsWithErrors
  }
  if (fixAttempt === 1) {
    allErrors = [...firstError, ...productsWithErrors]
    products = allErrors
  }
  const updates = new URLSearchParams()
  let changes = false

  products.forEach((product) => {
    const productLevelLimit = Number(product.productLevelLimit)
    // Set a total limit left for each product
    let productLevelLimitLeft = productLevelLimit || -1
    const variants = product.variants

    variants.forEach((variant) => {
      const variantId = variant.id
      if (!variantId) return
      const cartItemId = 'cart-item--' + variantId
      // Need to get qty from DOM element due to issue with service returning incorrect quantity in error response - TODO fix service response
      const qty = document.getElementById(cartItemId).getAttribute('qty')

      // Get the calculated purchase limit for this variant
      const maxVariantLimit = variant.maxVariantLimit
      let purchaseLimit = productLevelLimit || maxVariantLimit
      if (maxVariantLimit < productLevelLimit) purchaseLimit = maxVariantLimit
      const limit = purchaseLimit === null ? -1 : Number(purchaseLimit)  

      let targetQty
      if (qty <= limit) {
        targetQty = qty
      } else {
        targetQty = limit
      }

      // Check if there is a product level limit. If not, ignore 
      if (productLevelLimitLeft !== -1) {
        // Greedy algo to take all available. First come first serve. 
        if (targetQty > productLevelLimitLeft) {
          targetQty = productLevelLimitLeft
        }
        // Add the total here to the total amount used
        if (productLevelLimitLeft) {
          if (productLevelLimitLeft < targetQty) {
            productLevelLimitLeft = 0
          } else {
            productLevelLimitLeft -= targetQty
          }
        }
      }

      if (targetQty !== qty) changes = true
      updates.append(`updates[${variantId}]`, String(targetQty))
    })
  })

  if (changes) {
    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      credentials: 'same-origin',
      body: updates.toString()
    })
      .then(r => r.json())
      .then((data) => {
        if (data.status === 422 && fixAttempt === 0) {
          // If error response, product in cart has PL = 0. Resend error data to re-init this function. Function will combine first error and subsequent error to create 
          // correct payload with all products. Fixes issue where the "remove" icon is not actually removing the item
          fixAttempt++
          handlePurchaseLimitErrors(null, data)
        }
        // TODO - Need to fix issue with header-cart-count_md not being updated. Bug is happening in cart-drawer.js refreshSections too.
        const sections = [{ id: 'cart-drawer' },{ id: 'header-cart-count' }]
        window.refreshSections(sections)
      })
  }
}

/**
 * The function handles purchase limit errors by filtering and throwing the relevant errors.
 * @param product - The `product` parameter is an object that represents a product. It contains
 * properties such as `variant_id`, `component_ids`, `id`, etc.
 * @param parsedState - parsedState is an object that contains information about the state of the
 * Ajax API response. It may have properties like "errors" or "message" which contain error information.
 */

function handlePurchaseLimitErrors (product, parsedState) {
  const filtered_errors = []
  const errors = parsedState.errors ? parsedState.errors : parsedState.message
  const is_json_response = isJson(errors)

  if (is_json_response) {
    // Purchase Limits Errors
    const products_with_errors = JSON.parse(errors)
    let product_ids = []

    // Fix cart errors due to purchase limits and refresh cart section
    fixCartWithPurchaseLimitError(products_with_errors)

    if (product.component_ids) {
      product_ids = [...product.component_ids]
    } else {
      product_ids.push(product.id)
    }
    let hasVariantLevelErrors = false
    products_with_errors.forEach(product => {
      // Check for variant level errors
      if (product.variants.length) {
        product.variants.forEach(variant => {
          if (variant.maxVariantLimit !== null && variant.error !== '' && variant.cartLevelError !== '') {
            hasVariantLevelErrors = true
            if (product_ids.includes(product.id)) {
              filtered_errors.push(variant.error)
            } else {
              filtered_errors.push(variant.cartLevelError)
            }
          }
        })
      }
      // If no variant level errors but has product level errors
      if (product.productLevelLimit !== null && product.error !== '' && product.cartLevelError !== '' && !hasVariantLevelErrors) {
        if (product_ids.includes(product.id)) {
          filtered_errors.push(product.error)
        } else {
          filtered_errors.push(product.cartLevelError)
        }
      }
    })
  } else {
    // Standard shopify cart errors
    filtered_errors.push(parsedState.errors ? parsedState.errors : parsedState.description)
  }

  throw filtered_errors
}

function validateEmail (email) {
  // eslint-disable-next-line no-useless-escape
  const re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
  return re.test(email)
}

/**
 * A JavaScript function that gets as close as possible to emulating the "money" filter of Shopify Liquid
 * @param amountInCents
 * @returns {*|string}
 */
function formatMoney (amountInCents) {
  const format = window.shopCurrencyFormat

  if (isNaN(amountInCents) || !format) return ''
  const amount = parseFloat((amountInCents / 100).toString())

  const hasDecimals = (format.includes('amount') && !format.includes('amount_')) ||
    format.includes('amount_with_comma_separator') ||
    format.includes('amount_with_apostrophe_separator') ||
    format.includes('amount_with_space_separator') ||
    format.includes('amount_with_period_and_space_separator')
  const decimalPlaces = hasDecimals ? 2 : 0

  // The following Regex matches the positions in-between all groups of three digits, where a thousands separator should be inserted.
  const addSeparators = (num, separator = ',') => num.replace(/\B(?=(\d{3})+(?!\d))/g, separator)
  // The following Regex matches any of the supported replace tokens of the type "{{ amount_* }}" regardless of whitespaces within the curly braces.
  const applyCurrencyFormat = (num, format) => format.replace(/{{\s*amount.*?\s*}}/, num)

  let fixedAmount = amount.toFixed(decimalPlaces)

  // Choose format pattern
  if (format.includes('amount_with_comma_separator')) {
    fixedAmount = addSeparators(fixedAmount.replace('.', ','), '.')
  } else if (format.includes('amount_no_decimals_with_comma_separator')) {
    fixedAmount = addSeparators(parseInt(amount).toString(), '.')
  } else if (format.includes('amount_with_apostrophe_separator')) {
    fixedAmount = addSeparators(fixedAmount, '\'')
  } else if (format.includes('amount_no_decimals_with_space_separator')) {
    fixedAmount = addSeparators(parseInt(amount).toString(), ' ')
  } else if (format.includes('amount_with_space_separator')) {
    fixedAmount = addSeparators(fixedAmount, ' ')
  } else if (format.includes('amount_with_period_and_space_separator')) {
    fixedAmount = addSeparators(fixedAmount.replace('.', ' '), ' ')
  } else if (format.includes('amount_no_decimals')) {
    fixedAmount = parseInt(amount).toString()
  } else {
    fixedAmount = addSeparators(fixedAmount)
  }

  return applyCurrencyFormat(fixedAmount, format)
}

export { throttle, debounce, serializeForm, fetchConfig, getFocusableElements, trapFocus, removeTrapFocus, onKeyUpEscape, isJson, handlePurchaseLimitErrors, validateEmail, formatMoney }
